/**
 * §6.2 Behavior — system prompt (counter + starter templates), greeting
 * (counter), restrictions list, and model settings. llm_model and temperature
 * are cascaded-only (§6.3 cross-field rules): disabled with an explanatory
 * tooltip when Realtime is selected, never hidden.
 */

import { Button } from "../Button";
import { Field } from "../Field";
import { Input } from "../Input";
import { Select } from "../Select";
import { Slider } from "../Slider";
import { TextArea } from "../TextArea";
import { cx } from "../../lib/format";
import type { AgentConfig, LLMModel } from "../../lib/types";
import { PROMPT_TEMPLATES } from "./constants";
import { StringListEditor } from "./StringListEditor";
import type { DraftErrors } from "./validation";
import { Tooltip } from "./Tooltip";

const LLM_OPTIONS: Array<{ value: LLMModel; label: string }> = [
  { value: "gpt-4o-mini", label: "GPT-4o mini — fast and cheap (default)" },
  { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash — fast, long context" },
  { value: "gpt-4o", label: "GPT-4o — strongest, slower and pricier" },
];

function CharCounter({ value, max }: { value: number; max: number }) {
  return (
    <span className={cx("font-mono", value > max && "text-danger")}>
      {value.toLocaleString()} / {max.toLocaleString()}
    </span>
  );
}

export interface BehaviorSectionProps {
  config: AgentConfig;
  patch: (partial: Partial<AgentConfig>) => void;
  errors: DraftErrors;
}

export function BehaviorSection({ config, patch, errors }: BehaviorSectionProps) {
  const cascaded = config.voice_engine === "cascaded";
  // Temperature is a real knob on Gemini (passed through to the Live API's
  // InputParams) but not on OpenAI realtime, so the two gates differ.
  const realtime = config.voice_engine === "realtime";
  const modelTooltip =
    "Not used on this engine — the speech-to-speech model is the brain. Pick it in the Voice section.";
  const temperatureTooltip =
    "Not used on Realtime. Pick the model in the Voice section.";

  function applyTemplate(prompt: string) {
    if (
      config.system_prompt.trim() &&
      !window.confirm("Replace the current prompt with this template?")
    ) {
      return;
    }
    patch({ system_prompt: prompt });
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <Field
          label="Instructions"
          required
          error={errors.system_prompt}
          trailing={<CharCounter value={config.system_prompt.length} max={8000} />}
          help="What the agent is, what it does, and how it should behave on a call."
        >
          <TextArea
            mono
            rows={10}
            value={config.system_prompt}
            placeholder="You are the phone assistant for…"
            onChange={(event) => patch({ system_prompt: event.target.value })}
          />
        </Field>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted">Start from a template:</span>
          {PROMPT_TEMPLATES.map((template) => (
            <Button key={template.id} size="sm" onClick={() => applyTemplate(template.prompt)}>
              {template.label}
            </Button>
          ))}
        </div>
      </div>

      <Field
        label="Greeting"
        required
        error={errors.greeting}
        trailing={<CharCounter value={config.greeting.length} max={300} />}
        help="The agent's first line — it speaks first on incoming calls."
      >
        <Input
          value={config.greeting}
          placeholder="Hi, how can I help you today?"
          onChange={(event) => patch({ greeting: event.target.value })}
        />
      </Field>

      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-body">The agent must never…</span>
        <p className="text-xs text-muted">
          Hard limits, enforced separately from the instructions so they hold even in long calls.
        </p>
        <StringListEditor
          value={config.restrictions}
          onChange={(restrictions) => patch({ restrictions })}
          placeholder="e.g. quote prices that aren't in its knowledge"
          addLabel="Add restriction"
          itemLabel="Restriction"
        />
      </div>

      <div className="grid gap-4 border-t border-line pt-4 sm:grid-cols-2">
        <Tooltip content={modelTooltip} block disabled={cascaded}>
          <Field
            label="Model"
            help={cascaded ? "The brain behind responses on the Standard engine." : undefined}
          >
            <Select
              value={config.llm_model}
              disabled={!cascaded}
              options={LLM_OPTIONS}
              onChange={(event) => patch({ llm_model: event.target.value as LLMModel })}
            />
          </Field>
        </Tooltip>

        <Tooltip content={temperatureTooltip} block disabled={!realtime}>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-body">Creativity</span>
            <Slider
              value={config.temperature}
              onChange={(v) => patch({ temperature: v })}
              min={0}
              max={1}
              step={0.05}
              disabled={realtime}
              label="Creativity (temperature)"
              format={(v) => v.toFixed(2)}
            />
            {!realtime && (
              <p className="text-xs text-muted">Lower is more predictable; higher is more varied.</p>
            )}
          </div>
        </Tooltip>

        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <span className="text-xs font-medium text-body">Response length limit</span>
          <Slider
            value={config.max_response_tokens}
            onChange={(v) => patch({ max_response_tokens: v })}
            min={16}
            max={1024}
            step={16}
            label="Response length limit (tokens)"
            format={(v) => `${v} tok`}
          />
          <p className="text-xs text-muted">
            Keep this low — long responses sound slow and unnatural on a call.
          </p>
        </div>
      </div>
    </div>
  );
}
